import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormWrapper, FormRow, Label, InputField, TextArea, SubmitButton, PageContainer, ContentContainer, CancelButton, DateInputField } from "../styles/ComponentStyles";
import { CashFlowTypeDropdown } from "../components/Dropdowns";
import FlashMessage from "../components/FlashMessage";
import useCashFlows from "../hooks/useCashFlows";
import apiService from "../services/apiService";

const EditCashFlowPage = () => {
  const [flashMessage, setFlashMessage] = useState('');
  const { cash_flowId } = useParams();
  const { cash_flows, loading, error } = useCashFlows(1); // Assuming useCashFlows hook fetches cash_flows
  const navigate = useNavigate();

  const [cash_flowData, setCashFlowData] = useState(null);

  const [formData, setFormData] = useState({
    date_of_transaction: '',
    cash_flow_name: '',
    cash_flow_type: '',
    custom_type: '',
    amount: '',
    platform: '',
    payment_method: '',
    remarks: '',
    maintaining_balance: '',
  });

  const handleFlashMessage = (message) => {
    setFlashMessage(message);
  };

  // Set the cash_flow data when fetched
  useEffect(() => {
    if (cash_flowId && cash_flows.length > 0) {
      const selectedCashFlow = cash_flows.find(cash_flow => cash_flow._id === cash_flowId);
      setCashFlowData(selectedCashFlow);
    }
  }, [cash_flowId, cash_flows]);

  // Set formData when cash_flowData is available
  useEffect(() => {
    if (cash_flowData) {
      setFormData({
        date_of_transaction: cash_flowData.date_of_transaction,
        cash_flow_name: cash_flowData.cash_flow_name,
        cash_flow_type: cash_flowData.cash_flow_type,
        custom_type: cash_flowData.custom_type,
        amount: cash_flowData.amount,
        payment_method: cash_flowData.payment_method,
        platform: cash_flowData.platform,
        remarks: cash_flowData.remarks,
        maintaining_balance: cash_flowData.maintaining_balance || 0,
      });
    }
  }, [cash_flowData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCancel = () => {
    navigate('/cash_flows');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFlashMessage('');
    try {
      let payload = { ...formData };

      // Convert numeric values
      if (payload.amount) {
        payload.amount = parseFloat(payload.amount);
      } else if (payload.amount === '') {
        payload.amount = null;
      }

      if (payload.maintaining_balance) {
        payload.maintaining_balance = parseFloat(payload.maintaining_balance);
      } else if (payload.maintaining_balance === '') {
        payload.maintaining_balance = null;
      }

      const result = await apiService.putCashFlow(cash_flowId, payload);
      handleFlashMessage(result.data.message + " Redirecting. . .");
      setTimeout(() => {
        navigate("/cash_flows");
      }, 2000);
    } catch (error) {
      // Log the error message for debugging
      if (error.response) {
        console.error("Error response:", error.response.data);
        handleFlashMessage(error.response.data.detail);
      } else {
        console.error("Error:", error.message);
        handleFlashMessage(error.message);
      }
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading cash_flows data</div>;

  return (
    <PageContainer>
      <ContentContainer>
        {flashMessage && <FlashMessage message={flashMessage} />}
        <FormWrapper onSubmit={handleSubmit} className='cash-flows-form-wrapper'>
          <h3>Edit Cash Flow*</h3>
          <FormRow>
            <Label htmlFor="date_of_transaction">Date of Transaction</Label>
            <DateInputField
              name='date_of_transaction'
              value={formData.date_of_transaction}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="cash_flow_name">Cash Flow Name</Label>
            <InputField
              name='cash_flow_name'
              value={formData.cash_flow_name}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="cash_flow_type">Cash Flow Type</Label>
            <CashFlowTypeDropdown
              name='cash_flow_type'
              value={formData.cash_flow_type}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="custom_type">Custom Type</Label>
            <InputField
              name='custom_type'
              value={formData.custom_type}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="amount">Amount</Label>
            <InputField
              name='amount'
              value={formData.amount}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="payment_method">Payment Method</Label>
            <InputField
              name='payment_method'
              value={formData.payment_method}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="platform">Platform</Label>
            <InputField
              name='platform'
              value={formData.platform}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="maintaining_balance">Maintaining Balance (Threshold)</Label>
            <InputField
              name='maintaining_balance'
              type='number'
              value={formData.maintaining_balance}
              onChange={handleChange}
              placeholder="Minimum balance threshold"
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="remarks">Remarks</Label>
            <TextArea
              name="remarks"
              value={formData.remarks || ""}
              onChange={handleChange}
            />
          </FormRow>
          <SubmitButton type="submit" onClick={handleSubmit}>Save Changes</SubmitButton>
          <CancelButton type="button" onClick={handleCancel}>Cancel</CancelButton>
        </FormWrapper>
      </ContentContainer>
    </PageContainer>
  );
};

export default EditCashFlowPage;
